import events from "@/events/events"
import User from "@/types/User.interface"
import Credentials from "@/types/Credentials"
import {
    PasswordResetResponse,
    SimpleMessageResponse,
    SimpleTokenResponse,
    ResourceExistsResponse,
    VerifyPhoneResponse
} from "@/types/ApiResponses.interface"

export interface AuthConfirmPayload {
    password: string
    tenant_id: number
}
export interface AuthTokenPayload {
    userToken: string 
    twoFactorToken: string
}
export interface PasswordResetPayload {
    email: string
    firstname: string
    lastname: string
    mobilephone: string
    password: string
    token: string
}

interface State {
    resellerLink: string
    token: string
    twoFactorToken: string
    user: User
    userTmp: User
}

const defaults = () => {
    return {
        resellerLink: null, //  The user's reseller link
        token: null,        //  The user's api token
        user: null,         //  The currently authenticated user
        userTmp: null       //  A temporary user returned from
    }
}

export default {
    state: defaults(),
    mutations: {
        authTokenSet(state: State, token: string): void {
            //  Update the state
            state.token = token

            //  If the token exists...
            if (token) {
                //  Add token to Authorization header
                window.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

                //  Save token in local storage
                localStorage.setItem('token', token)
            }

            //  Else, if the token does NOT exist
            else {
                //  Remove token form Authorization header
                window.axios.defaults.headers.common['Authorization'] = null

                //  Remove token from local storage
                localStorage.removeItem('token')
            }

            //  Publish the event
            events.onAuthTokenSet(token)
        },
        authTwoFactorTokenSet(state: State, twoFactorToken: string): void {
            //  Update the state
            state.twoFactorToken = twoFactorToken

            //  If the token exists...
            if (twoFactorToken) {
                 //  Add token to our header which is used by our 2fa middleware to ensure 2fa has been passed
                window.axios.defaults.headers.common['X-Secondary-Token'] = twoFactorToken

                //  Save token in local storage
                localStorage.setItem('two_factor_token', twoFactorToken)
            }

            //  Else, if the token does NOT exist
            else {
                //  Remove token from our header
                window.axios.defaults.headers.common['X-Secondary-Token'] = null

                //  Remove token from local storage
                localStorage.removeItem('two_factor_token')
            }

            // Publish the event
            events.onTwoFactorTokenSet(twoFactorToken)
        },
        authUserSet(state: State, user: User): void {
            //  If the authenticated user is already logged in
            const isNew = (!state.user && user) || (user && state.user.id != user.id)

            // Set the user
            state.user = user

            //  If the user is null, publish the logout event
            if (!user) events.onUserLogout()

            //  Else, if the user is new, publish the login event
            else if (isNew) events.onUserLogin(state.user)
        },
        authUserTmpSet(state: State, user: User) {
            state.userTmp = user
        },
        resellerLinkSet(state: State, link: string): void {
            state.resellerLink = link
        }
    },
    actions: {
        authUserConfirm({}, obj: AuthConfirmPayload): Promise<boolean> {
            return window.axios.post('/auth/user/confirm', obj).then(() => {
                return true
            }).catch(() => {
                return false
            })
        },
        authUserDelete({ commit }, user: User): User {
            return window.axios.delete(`/auth/user/${user.id}`).then(response => {
                commit('authUserSet', null)
                return response.data
            }).catch(() => {
                return null
            })
        },
        authUserGet({ commit, getters }, payload: AuthTokenPayload): Promise<User> {
            //  Set the api token
            commit('authTokenSet', payload.userToken)
            commit('authTwoFactorTokenSet', payload.twoFactorToken)

            //  If a token doesn't exist exit, we don't check for twoFactor here since we don't allow users to get to the 
            //  two factor workflow without passing basic auth first which is what this is responsible for, we may still pass the two
            //  factor token however to this function in the case the  user has been authorized before and is for example, refreshing the page
            if (!getters.token) return

            return window.axios.post('/auth/user', payload).then(response => {
                commit('authUserSet', response.data.user)
                return response.data
            }).catch(() => {
                return null
            })
        },
        authUserGetTwoFactor({ commit, getters }, twoFactorToken: string): Promise<User> {
            //  Set the api token
            commit('authTwoFactorTokenSet', twoFactorToken)

            if (!getters.twoFactorToken) return

            const payload = {
                'twoFactorToken': twoFactorToken
            }

            return window.axios.post('/auth/user/two-factor', payload).then(response => {
                commit('authUserSet', response.data)
                return response.data
            }).catch(() => {
                return null
            })
        },
        authUserPut({ commit }, user: User): User {
            return window.axios.put('/auth/user', user).then(response => {
                commit('authUserSet', response.data.token)
                return response.data
            }).catch(() => {
                return null
            })
        },
        authUserRegister(_, user: User): User {
            return window.axios.put('/auth/user/register', user).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        authUserTmpGet({ commit }, token: string): User {
            return window.axios.get('/auth/once/' + token).then(response => {
                commit('authUserTmpSet', response.data)
                return response.data
            }).catch(() => {
                return null
            })
        },
        authPasswordReset(_, payload: PasswordResetPayload): PasswordResetResponse {
            return window.axios.put('/auth/password/reset', payload).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        authPasswordResetEmailSend(_, email: string): SimpleMessageResponse {
            return window.axios.post('/auth/password/reset-send', { email: email }).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        authVerifyEmail({ commit }, code: string): User {
            return window.axios.post('/auth/verify/email', code).then(response => {
                //  Update the auth user
                commit('authUserSet', response.data.user)

                //  Set their two factor token since email auth passed
                commit('authTwoFactorTokenSet', response.data.twoFactorToken)
                return response.data.user
            }).catch(() => {
                return null
            })
        },
        authVerifyEmailSend(): SimpleMessageResponse {
            return window.axios.get('/auth/verify/email').then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        authVerifyPhone({ commit }, code: string): VerifyPhoneResponse {
            return window.axios.post('/auth/verify/phone', code).then(response => {
                commit('authUserSet', response.data.user)
                return response.data
            }).catch(() => {
                return null
            })
        },
        authVerifyPhoneSend(): SimpleMessageResponse {
            return window.axios.get('/auth/verify/phone').then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        invitationUserPost (_, payload: object): Promise<User> {
            //  Assemble the API endpoint
            const url = `auth/invitations/user`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Get our auth user
                return response.data
            }).catch(() => null)
        },
        linkAccounts({ dispatch }, creds: Credentials): User {
            return window.axios.post('/auth/link-accounts', creds).then(response => {
                return dispatch('authUserGet', response.data.token)
            }).catch(() => {
                return null
            })
        },
        login({ dispatch }, creds: Credentials): User {
            return window.axios.post('/auth/login', creds).then(response => {
                return dispatch('authUserGet', response.data)
            }).catch((err) => {
                //  If this is an authentication error, return auth err
                if (err.response.status === 401 || err.response.status == 429) return { error: err.response }

                //  Throw the error
                else throw err
            })
        },
        logout() {
            return window.axios.post('/auth/logout').then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        mobilephoneExists(_, mobilephone: string): ResourceExistsResponse {
            return window.axios.get(`auth/mobilephone-exists?mobilephone=${mobilephone}`).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        emailExists(_, email: string): ResourceExistsResponse {
            //  Create our payload
            const payload = {
                email: email
            }

            //  Convert this into query params (this parses special characters)
            const query = new URLSearchParams(payload).toString()

            //  Send the request
            return window.axios.get(`auth/email-exists?${query}`).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        },
        register({ dispatch }, user: User): User {
            return window.axios.post('/auth/register', user).then(response => {
                return dispatch('authUserGet', response.data)
            }).catch(() => {
                return null
            })
        },
        resellerGet({ commit }): string {
            return window.axios.get(`/auth/reseller`).then(response => {
                commit('resellerLinkSet', response.data)
                return response.data
            }).catch(() => {
                return null
            })
        },
        resellerPost(_, email: Object): string {
            return window.axios.post('/auth/reseller', email).then(response => {
                return response.data
            }).catch(() => {
                return null
            })
        }
    },
    getters: {
        authenticated(state: State): boolean {
            const authenticated = state.token != null && state.user != null
            return authenticated
        },
        authIsAdmin(state: State, getters): boolean {
            //  Get the currently authenticated user
            const user = state.user

            //  If the user does NOT exist, do nothing
            if (!user) return

            //  Get the currently set tenant
            const tenant = getters.tenant

            //  If the tenant does NOT exist, do nothing
            if (!tenant) return

            //  Else, check if the user is the admin
            const isAdmin = tenant.admin_user_id === user.id

            //  Return the result
            return isAdmin
        },
        authIsSupport(state: State, getters): boolean {
            //  Get the currently authenticated user
            const user = state.user

            //  If the user does NOT exist, return false
            if (!user) return false

            //  Get the currently set tenant
            const tenant = getters.tenant

            //  If the tenant does NOT exist, return false
            if (!tenant) return false

            //  Else, iterate through the user's memberships
            for (let i = 0; i < user.memberships.length; i++) {
                //  Ref to current membership
                let mem = user.memberships[i]
                //  Check if tenant id matches
                if (mem.tenant_id == tenant.id) {
                    //  Check if role has permission 4
                    return mem.role.permission === 4
                }
            }

            //  Else, no matches return false
            return false
        },
        isAdminOfAny(state: State, getters): boolean {
            for (let i = 0; i < state.user.memberships.length; i++) {
                let role = state.user.memberships[i].role
                if (role.permission === 3) return true
            }
            return false
        },
        isSupportUser: (state: State, getters) => (user: User): boolean => {
            //  Find the membership for the current tenant
            const current = user.memberships.find(x => x.tenant_id === getters.tenant.id)

            //  If there is no current, return false
            if (!current) return false

            //  Else, check if this role is "Support"
            if (current.role.name === 'Support' && current.role.permission === 4) return true

            return false
        },
        resellerLink(state: State) {
            return state.resellerLink
        },
        token(state: State) {
            return state.token
        },
        twoFactorToken(state: State) {
            return state.twoFactorToken
        },
        user(state: State) {
            return state.user
        },
        userHasPermission: (state: State, getters) => (permission: number): boolean => {
            //  Get the current user
            const user: User = getters.user

            //  If the user doesn't exist return false
            if (!user) return false

            //  If the permission is 0, return true
            if (permission === 0) return true

            //  Get the current tenant
            const tenant = getters.tenant

            //  If the tenant doesn't exist, return false
            if (!tenant) return false

            //  Get the user's tenant membership
            const membership = user.memberships.find(x => x.tenant_id == tenant.id)

            //  If no membership is found, return false
            if (!membership) return false

            //  Else, check the user's permission level against the inputed permission
            if (membership.role.permission >= permission) return true

            return false
        },
        userTmp(state: State): User {
            return state.userTmp
        }
    }
}
